import { getProductFinalPriceWithTax } from '$lib/js/helpers'
import { PUBLIC_BIXOTO_MEDIA, PUBLIC_MAGENTO_ENDPOINT, PUBLIC_STRAPI_MEDIA } from '$env/static/public'
import { gt } from 'svelte-i18n-lingui'
import type { BaseProduct, FullBrand, FullProduct } from '$lib/js/elasticsearch/types'
import type { BlogPost, HeroSection } from '$lib/js/strapi/types'
import type { Review } from '$lib/js/reviewsio/types'
import type { SiteScope } from '$lib/js/helpers/i18n'

export function serializeSchema(thing: Record<string, any>) {
	return `<script type="application/ld+json">${JSON.stringify(thing, null)}</script>`
}

/**
 * Get merchant data
 **/
export function getMerchantSchema(product: FullProduct | BaseProduct, options: {
	brand?: FullBrand,
	reviews?: Review[],
	scope: SiteScope,
}) {
	let price = Math.round((getProductFinalPriceWithTax(product) + Number.EPSILON) * 100) / 100
	let shipping_info: Record<string, any>

	const { brand, reviews, scope } = options

	let merchantSchema: Record<string, any> = {
		'@context': 'https://schema.org',
		'@type': 'Product',
		'name': product.name,
		'sku': product.sku,
		'description': (<FullProduct>product).description || product.name,
		'offers': {
			'@type': 'https://schema.org/Offer',
			'price': price,
			'url': `${PUBLIC_MAGENTO_ENDPOINT}/${scope}/p/${product.url_key}`,
			'priceCurrency': 'EUR',
			'availability': 'https://schema.org/InStock', // TODO: make this dynamic
			'itemCondition': 'https://schema.org/NewCondition', // TODO: make this dynamic
			'priceValidUntil': '2025-12-31', // TODO: make this dynamic
			'shippingDetails': '',
			'hasMerchantReturnPolicy': {
				'@type': 'MerchantReturnPolicy',
				'applicableCountry': scope.toLocaleUpperCase(),
				'returnPolicyCategory': 'https://schema.org/MerchantReturnFiniteReturnWindow',
				'merchantReturnDays': 14, // Should this depend on the scope?
				'returnMethod': 'https://schema.org/ReturnByMail',
			},
		},
		'productID': product.sku,
		'gtin13': product.ean13,
		'category': product.category && product.category.length > 0 ? product.category[product.category.length - 1].name : '',
	}

	// Some products have no image (e.g. BigBuy's variations)
	if (product.resized_image) {
		merchantSchema['image'] = `${PUBLIC_BIXOTO_MEDIA}${product.resized_image.path}`
	}

	if (price > 69) {
		shipping_info = {
			'@type': 'OfferShippingDetails',
			'shippingRate': {
				'@type': 'MonetaryAmount',
				'value': '0',
				'currency': 'EUR',
			},
			'shippingDestination': {
				'@type': 'DefinedRegion',
				'addressCountry': 'ES',
			},
			'deliveryTime': {
				'@type': 'ShippingDeliveryTime',
				'handlingTime': {
					'@type': 'QuantitativeValue',
					'minValue': 0,
					'maxValue': 1,
					'unitCode': 'DAY',
				},
				'transitTime': {
					'@type': 'QuantitativeValue',
					'minValue': 0,
					'maxValue': 4,
					'unitCode': 'DAY',
				},
			},
		}
	} else {
		shipping_info = {
			'@type': 'OfferShippingDetails',
			'shippingRate': {
				'@type': 'MonetaryAmount',
				'value': 3.99,
				'currency': 'EUR',
			},
			'shippingDestination': {
				'@type': 'DefinedRegion',
				'addressCountry': 'ES',
			},
			'deliveryTime': {
				'@type': 'ShippingDeliveryTime',
				'handlingTime': {
					'@type': 'QuantitativeValue',
					'minValue': 0,
					'maxValue': 1,
					'unitCode': 'DAY',
				},
				'transitTime': {
					'@type': 'QuantitativeValue',
					'minValue': 0,
					'maxValue': 4,
					'unitCode': 'DAY',
				},
			},
		}
	}

	merchantSchema.offers.shippingDetails = shipping_info

	if (brand && brand.page_title) {
		merchantSchema.brand = {
			'@type': 'Brand',
			'name': brand.page_title,
		}
		merchantSchema.manufacturer = brand.page_title
	}

	if (product.reviews_number && product.reviews_number > 0 && product.rating_average && product.rating_average >= 3 && reviews) {
		let allRevs: Record<string, any>[] = []

		for (const review of reviews) {
			allRevs.push({
				'@type': 'Review',
				'reviewRating': {
					'@type': 'Rating',
					'ratingValue': review.rating,
					'bestRating': 5,
					'worstRating': 1
				},
				'author': {
					'@type': 'Person',
					'name': review.author.name,
				},
				'reviewBody': review.comments || '',
				'datePublished': review.date_created || new Date().toISOString(),
				'publisher': {
					'@type': 'Organization',
					'name': 'Bixoto'
				}
			})
		}

		merchantSchema.review = allRevs
		merchantSchema.aggregateRating = {
			'@type': 'AggregateRating',
			'ratingValue': product.rating_average,
			'ratingCount': product.reviews_number,
			'bestRating': 5,
			'worstRating': 1
		}
	}

	return merchantSchema
}

/**
 * Get post structured data
 **/
export function getPostSchema(post: BlogPost) {
	return {
		'@context': 'https://schema.org',
		'@type': 'Article',
		'headline': post.page_title,
		'description': post.post_excerpt || post.hero_section?.description,
		'image': [
			PUBLIC_STRAPI_MEDIA + post.hero_section.bg_img?.url,
		],
		'datePublished': post.publishedAt,
		'dateModified': post.updatedAt,
		'author': [{
			'@type': 'Organization',
			'name': 'Bixoto',
			'url': PUBLIC_MAGENTO_ENDPOINT,
		}],
		'publisher': {
			'@type': 'Organization',
			'name': 'Bixoto',
			'logo': {
				'@type': 'ImageObject',
				'url': 'https://bixoto.com/images/logo-bixoto.png'
			}
		},
		'mainEntityOfPage': {
			'@type': 'WebPage',
			'@id': `${PUBLIC_MAGENTO_ENDPOINT}/blog/${post.page_slug}`
		},
		'articleSection': 'Blog',
		'keywords': post.page_title,
		'wordCount': post.post_excerpt?.length || 0
	}
}

/* Get page structured data */
export function getPageSchema(url: URL, data: { page_data?: { hero_section?: HeroSection } }) {
	let title = data?.page_data?.hero_section?.page_title

	return {
		'@context': 'https://schema.org/',
		'@type': 'BreadcrumbList',
		'itemListElement': [{
			'@type': 'ListItem',
			'item': {
				'id': url.href,
				'type': 'Thing',
				'name': title || 'Bixoto',
			},
			'position': '1',
		}],
	}
}

/* Get breadcrumbs structured data */
export function getBreadcrumbsSchema(product_categories: { name: string, position: number }[]) {
	return {
		'@context': 'https://schema.org/',
		'@type': 'BreadcrumbList',
		'itemListElement': product_categories.map(category => ({
			'@type': 'ListItem',
			'item': {
				'id': category.name,
				'type': 'Thing',
				'name': category.name,
			},
			'position': category.position,
		})),
	}
}

export function getFAQSchema(questions: { question: string; answer: string }[]) {
	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		'mainEntity': questions.map(q => ({
			'@type': 'Question',
			'name': q.question,
			'acceptedAnswer': {
				'@type': 'Answer',
				'text': q.answer
			}
		}))
	}
}

export function getWebsiteSchema({ scope }: { scope: SiteScope }): Record<string, any> {
	return {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		'name': 'Bixoto',
		'url': 'https://www.bixoto.com',
		'description': gt`Your sustainable online store`,
		'logo': 'https://bixoto.com/images/logo-bixoto.png',
		'image': 'https://bixoto.com/images/bg-brand.jpg',
		'sameAs': [
			'https://www.facebook.com/bixoto',
			'https://twitter.com/bixoto_com',
			'https://www.instagram.com/bixoto_com'
		],
		'address': {
			'@type': 'PostalAddress',
			'streetAddress': 'Ronda Sant Pere 52',
			'addressLocality': 'Barcelona',
			'postalCode': '08010',
			'addressCountry': 'ES'
		},
		'contactPoint': {
			'@type': 'ContactPoint',
			'telephone': '+34-613017716',
			'contactType': 'customer service',
			'availableLanguage': ['Spanish', 'English']
		},
		'foundingDate': '2020',
		'numberOfEmployees': '50-100',
		'areaServed': 'Spain',
		'hasOfferCatalog': {
			'@type': 'OfferCatalog',
			'name': 'Product Catalog',
			'itemListElement': []
		}
	}
}

export function getSearchResultsSchema(query: string, results: any[], totalResults: number): Record<string, any> {
	return {
		'@context': 'https://schema.org',
		'@type': 'SearchResultsPage',
		'name': `Search results for "${query}"`,
		'description': `Search results for "${query}" - ${totalResults} products found`,
		'url': `https://bixoto.com/search?q=${encodeURIComponent(query)}`,
		'potentialAction': {
			'@type': 'SearchAction',
			'target': {
				'@type': 'EntryPoint',
				'urlTemplate': 'https://bixoto.com/search?q={search_term_string}'
			},
			'query-input': 'required name=search_term_string'
		},
		'mainEntity': {
			'@type': 'ItemList',
			'numberOfItems': totalResults,
			'itemListElement': results.slice(0, 10).map((item, index) => ({
				'@type': 'ListItem',
				'position': index + 1,
				'item': {
					'@type': 'Product',
					'name': item.name,
					'url': `https://bixoto.com/p/${item.url_key}`,
					'image': item.resized_image?.path ? `https://bixoto.com${item.resized_image.path}` : undefined,
					'description': item.description || item.name
				}
			}))
		}
	}
}

export function getLocalBusinessSchema({ scope }: { scope: SiteScope }): Record<string, any> {
	return {
		'@context': 'https://schema.org',
		'@type': 'LocalBusiness',
		'name': 'Bixoto',
		'url': 'https://www.bixoto.com',
		'description': gt`Your sustainable online store`,
		'logo': 'https://bixoto.com/images/logo-bixoto.png',
		'image': 'https://bixoto.com/images/bg-brand.jpg',
		'address': {
			'@type': 'PostalAddress',
			'streetAddress': 'Ronda Sant Pere 52',
			'addressLocality': 'Barcelona',
			'postalCode': '08010',
			'addressCountry': 'ES'
		},
		'telephone': '+34-613017716',
		'email': 'info@bixoto.com',
		'openingHours': 'Mo-Fr 09:00-18:00',
		'priceRange': '€€',
		'paymentAccepted': ['Cash', 'Credit Card', 'PayPal'],
		'currenciesAccepted': 'EUR',
		'areaServed': 'Spain',
		'hasOfferCatalog': {
			'@type': 'OfferCatalog',
			'name': 'Product Catalog',
			'itemListElement': []
		}
	}
}
